
import {concat as observableConcat, of, throwError} from 'rxjs';

import {toArray, map, catchError} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {FinanceConnectionStatusUpdate} from './finance-connection-status-update.model';
import { FinanceConnection } from './finance-connection.model';
import { ProjectShareFailure } from './project-share-failure.model';
import { Observable } from 'rxjs';
import { CONNECTION_STATUS } from '@shared/connection-statuses';
import { environment } from '@environments/environment';
import { ProjectConnectionStatus, ProjectConnectionInteractionService } from '@project/detail/sharing/shared/project-connection-interaction.service';
import { User } from '@user/user.model';
import { RestApiWrapperService } from '@shared/services/rest-api-wrapper.service';
import { BaseRestApiService } from '@shared/services/base-rest-api.service';

@Injectable()
export class FinanceConnectionStatusUpdateService extends BaseRestApiService {
  private projectGroups = environment.serverUri + `api/${environment.apiVersion}/project-groups`;
  private connectionStatusMap = new Map();
  private projectConnectionStatus = new ProjectConnectionStatus();

  constructor(_restApiWrapperService: RestApiWrapperService,
              private _projectConnectionInteractionService: ProjectConnectionInteractionService) {
    super(_restApiWrapperService);
    this.connectionStatusMap
      .set('level2DateTime', CONNECTION_STATUS[CONNECTION_STATUS.PERMIT_LEVEL_2])
      .set('level3DateTime', CONNECTION_STATUS[CONNECTION_STATUS.PERMIT_LEVEL_3])
      .set('level4DateTime', CONNECTION_STATUS[CONNECTION_STATUS.PERMIT_LEVEL_4])
      .set('level5DateTime', CONNECTION_STATUS[CONNECTION_STATUS.PERMIT_LEVEL_5]);
  }

  private getLevels(projectGroupId: string, financeConnectionId: string) {
    const financeConnection = new FinanceConnection({});
    const regex = new RegExp('level([2-9]|[1-9]\\d\+)DateTime');
    const properties = Object.getOwnPropertyNames(financeConnection);
    let levels = properties.filter((property: string) => regex.test(property));
    levels = levels.map(level => this.connectionStatusMap.get(level));
    return levels;
  }

  public createUpdate(projectGroupId: string, financeConnectionStatusUpdate: FinanceConnectionStatusUpdate): Observable<FinanceConnection> {
    if (financeConnectionStatusUpdate.id) {
      return this.put<any>(
        `${this.projectGroups}/${projectGroupId}/finance/connections/${financeConnectionStatusUpdate.id}`,
        JSON.stringify(financeConnectionStatusUpdate)
      )
      .pipe(
        map(result => new FinanceConnection(result))
      );
    }
    return this.post<any>(
      `${this.projectGroups}/${projectGroupId}/finance/connections`,
      JSON.stringify(financeConnectionStatusUpdate)
      )
      .pipe(
        map(result => new FinanceConnection(result))
      );
  }

  releaseToInvestor(projectGroupId: string, financeConnection: FinanceConnection, currentUser: User) {
    return this._releaseToInvestor(projectGroupId, financeConnection.id);
  }

  private _releaseToInvestor(projectGroupId: string, financeConnectionId: string) {
    const levels = this.getLevels(projectGroupId, financeConnectionId);
    const financeConnectionStatusUpdates$: Observable<FinanceConnectionStatusUpdate | FinanceConnection>[] = [];
    levels.push(CONNECTION_STATUS[CONNECTION_STATUS.ACCEPT]);
    levels.forEach(level => {
      financeConnectionStatusUpdates$.push(this._save(projectGroupId, financeConnectionId, level));
    });
    return observableConcat(...financeConnectionStatusUpdates$).pipe(toArray());
  }

  private _save(projectGroupId, financeConnectionId, level): Observable<FinanceConnectionStatusUpdate> {
    if (financeConnectionId) {
      return this.put<any>(`${this.projectGroups}/${projectGroupId}/finance/connections/${financeConnectionId}`,
        new FinanceConnectionStatusUpdate({
          id: financeConnectionId,
          financeConnectionStatus: level
        })
      );
    }
     return this.post<any>(`${this.projectGroups}/${projectGroupId}/finance/connections`,
        new FinanceConnectionStatusUpdate({
          id: financeConnectionId,
          financeConnectionStatus: level
        })
      );
  }

  /**
   * Shares every project of the portfolio with the investor in one all-or-nothing call. The API
   * checks all projects against the program submission rules first: if any is blocked nothing is
   * shared and the blocked projects come back as failures, so the caller can stop before the
   * portfolio itself is shared.
   */
  shareProjectsWithInvestor(projectGroupId: string, recipientId: string): Observable<ProjectShareFailure[]> {
    return this.post<unknown>(`${this.projectGroups}/${projectGroupId}/share-projects`, {recipientId})
      .pipe(
        map((): ProjectShareFailure[] => []),
        catchError((error: HttpErrorResponse) => {
          const blockedProjects = error?.error?.blockedProjects;
          if (error?.status === 400 && Array.isArray(blockedProjects)) {
            return of(blockedProjects as ProjectShareFailure[]);
          }
          return throwError(() => error);
        })
      );
  }
}
